import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { RouterModule } from '@angular/router';
import type { HistoryFindInterface } from '@zaoblako/shared/common/dtos/history/history-find.dto';
import type { HistoryPageInterface } from '@zaoblako/shared/common/dtos/history/history-page.dto';
import type { HistoryTargetFindInterface } from '@zaoblako/shared/common/dtos/history/history-target-find.dto';
import type { PaginationRequiredQueryInterface } from '@zaoblako/shared/common/dtos/pagination-required-query.dto';
import type { Connection1cFindInterface } from '@zaoblako/shared/modules/integration/dtos/connection-1c/connection-1c-find.dto';
import { BasisCostCenterService } from '@zaoblako/web/core/services/basis/basis-cost-center.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { FilialService } from '@zaoblako/web/core/services/finance/filial.service';
import { UnitService } from '@zaoblako/web/core/services/finance/unit.service';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { ButtonModule } from 'primeng/button';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-finance-history-list',
  templateUrl: './finance-history-list.component.html',
  imports: [CommonModule, ButtonModule, OverlayPanelModule, SpinnerComponent, ProgressSpinnerModule, RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FinanceHistoryListComponent {
  @Input() public financeType: TypeFormEnum;

  public historyItems: HistoryFindInterface[];

  public queryParamsHistory: PaginationRequiredQueryInterface = {
    limit: 100,
    offset: 0
  };

  public loadingHistory = false;
  public paginationLoadingHistory = false;
  public countItemsHistory: number;

  public constructor(
    private readonly cdRef: ChangeDetectorRef,
    private readonly directionService: DirectionService,
    private readonly unitService: UnitService,
    private readonly filialService: FilialService,
    private readonly basisCostCenterService: BasisCostCenterService,
    private readonly formStoreService: FormStoreService
  ) {}

  public loadHistory({ concat } = { concat: false }) {
    if (!concat) {
      this.loadingHistory = true;
    }

    let service: Observable<HistoryPageInterface>;
    if (this.financeType === TypeFormEnum.DIRECTION) {
      service = this.directionService.history(this.queryParamsHistory);
    } else if (this.financeType === TypeFormEnum.UNIT) {
      service = this.unitService.history(this.queryParamsHistory);
    } else if (this.financeType === TypeFormEnum.FILIAL) {
      service = this.filialService.history(this.queryParamsHistory);
    } else if (this.financeType === TypeFormEnum.COST_CENTER) {
      service = this.basisCostCenterService.getAllHistory(this.queryParamsHistory);
    }

    service.subscribe((data) => {
      this.queryParamsHistory.limit = data.limit;
      this.queryParamsHistory.offset = data.offset;
      this.countItemsHistory = data.count;
      this.historyItems = concat ? (this.historyItems as any[]).concat(data.items) : data.items;
      this.paginationLoadingHistory = false;
      this.loadingHistory = false;
      this.cdRef.markForCheck();
    });
  }

  public onScrollEndHistory(target: HTMLElement): void {
    if (target.offsetHeight + target.scrollTop >= target.scrollHeight) {
      if (this.historyItems.length < this.countItemsHistory && !this.loadingHistory && !this.paginationLoadingHistory) {
        this.paginationLoadingHistory = true;
        this.queryParamsHistory.offset += this.queryParamsHistory.limit;
        this.loadHistory({ concat: true });
      }
    }
  }

  public hideHistory() {
    this.queryParamsHistory.offset = 0;
  }

  public showFinance(target: HistoryTargetFindInterface) {
    if (this.financeType === TypeFormEnum.DIRECTION) {
      this.formStoreService.editForm(
        target.group ? TypeFormEnum.DIRECTION_GROUP : TypeFormEnum.DIRECTION,
        <number>target.key,
        {
          headerText: 'Редактирование бюджетной статьи',
          widthSize: 'form-sidebar-small',
          isShowShare: true
        }
      );
    } else if (this.financeType === TypeFormEnum.UNIT) {
      this.formStoreService.editForm(TypeFormEnum.UNIT, <number>target.key, {
        headerText: 'Редактирование бизнес единицы',
        widthSize: 'form-sidebar-small',
        isShowShare: true
      });
    } else if (this.financeType === TypeFormEnum.FILIAL) {
      this.formStoreService.editForm(TypeFormEnum.FILIAL, <number>target.key, {
        headerText: 'Редактирование филиала',
        widthSize: 'form-sidebar-small',
        isShowShare: true
      });
    } else if (this.financeType === TypeFormEnum.COST_CENTER) {
      this.formStoreService.editForm(TypeFormEnum.COST_CENTER, <number>target.key, {
        headerText: 'Редактирование центр затрат',
        widthSize: 'form-sidebar-small',
        isShowShare: true
      });
    }
  }

  public showDatabase(database: Connection1cFindInterface) {
    this.formStoreService.editForm(TypeFormEnum.DATABASE_1C, database.id, {
      headerText: 'Редактирование базы 1С',
      widthSize: 'form-sidebar-small',
      isShowShare: true
    });
  }
}
